import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Brain } from 'lucide-react';
import Container from '../ui/Container';

const links = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Plans', path: '/plans' },
  { name: 'FAQ', path: '/faq' },
  { name: 'Contact', path: '/contact' },
];

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  
  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <header 
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-background/95 backdrop-blur-sm shadow-md' : 'bg-transparent'
      }`}
    >
      <Container>
        <nav className="flex items-center justify-between py-4">
          <Link 
            to="/"
            className="flex items-center space-x-3 group"
          >
            <div className="relative w-10 h-10">
              <Brain 
                size={40} 
                className="absolute top-0 left-0 text-primary transform transition-all duration-300 group-hover:animate-[pulse_1.5s_ease-in-out_infinite]"
                strokeWidth={1.5}
              />
              <div className="absolute inset-0 bg-primary/10 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-[ping_1.5s_ease-in-out_infinite] transition-opacity duration-300"></div>
            </div>
            <span className="text-xl font-bold">Product Leap AI</span>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {links.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`py-2 text-base font-medium transition-colors duration-300 ${
                  location.pathname === link.path 
                    ? 'text-primary' 
                    : 'text-neutral-300 hover:text-primary'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2 -mr-2 text-text focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background rounded-lg"
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </Container>
      
      {/* Mobile Navigation */}
      <div 
        className={`md:hidden fixed inset-x-0 top-[73px] bg-background/95 backdrop-blur-sm border-b border-neutral-800 transition-all duration-300 ease-in-out ${
          isOpen ? 'translate-y-0 opacity-100 pointer-events-auto' : '-translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <Container>
          <nav className="py-6 space-y-4">
            {links.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 text-lg font-medium transition-colors duration-300 ${
                  location.pathname === link.path 
                    ? 'text-primary' 
                    : 'text-neutral-300 hover:text-primary'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </Link>
            ))}
          </nav>
        </Container>
      </div>
    </header>
  );
};

export default Navbar;